import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { usePagination } from "@/hooks/usePagination";
import { OBJECTION_LABELS } from "@/data/appointmentOutcomeTypes";
import { lifecycleToneClasses } from "@/data/situational/outcomeCapture";
import { ChevronRight, ShieldCheck, MessageSquareWarning } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import {
  CheckCircle, XCircle, RotateCcw, AlertTriangle,
  Search, CalendarIcon, Filter, TrendingUp, DollarSign,
  FileText, Users, BarChart3,
} from "lucide-react";
import {
  RESULT_TYPE_CONFIG, NO_SALE_REASON_LABELS, NO_DEMO_REASON_LABELS,
  type AppointmentResult, type AppointmentResultType,
} from "@/data/appointmentResultTypes";
import { PaginationBar } from "@/components/PaginationBar";
import { calculateCloseRatePercent } from "@/lib/metricsUtils";
import { getResultMetrics, getRepPerformanceSummary, getDisplayPrice } from "@/data/appointmentResultSelectors";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";

const RESULT_ICONS: Record<AppointmentResultType, React.ElementType> = {
  sold: CheckCircle, no_sale: XCircle, rescheduled: RotateCcw, no_demo: AlertTriangle,
};

const repLabel = (value: string) => resolveProfileDisplayName(value, undefined, "Unassigned");

export default function AppointmentResults() {
  const { appointmentResults, appointmentOutcomeExtensions, jobs } = useAppData();
  const navigate = useNavigate();

  const extensionsByResultId = useMemo(() => {
    const m = new Map<string, typeof appointmentOutcomeExtensions[number]>();
    for (const ext of appointmentOutcomeExtensions) m.set(ext.appointmentResultId, ext);
    return m;
  }, [appointmentOutcomeExtensions]);

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();


  const reps = useMemo(
    (): string[] =>
      [...new Set(appointmentResults.map((r: AppointmentResult) => String(r.assignedRep || "").trim()).filter(Boolean))].sort(),
    [appointmentResults],
  );
  const sources = useMemo((): string[] => [...new Set(appointmentResults.map((r: AppointmentResult) => r.source).filter(Boolean))].sort(), [appointmentResults]);

  const filtered = useMemo(() => {
    let list = appointmentResults;
    if (tab !== "all") list = list.filter(r => r.resultType === tab);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.customerName.toLowerCase().includes(q) || repLabel(r.assignedRep).toLowerCase().includes(q));
    }
    if (repFilter !== "all") list = list.filter(r => r.assignedRep === repFilter);
    if (sourceFilter !== "all") list = list.filter(r => r.source === sourceFilter);
    if (reasonFilter !== "all") list = list.filter(r => (r.noSaleReason || r.noDemoReason) === reasonFilter);
    if (dateFrom) list = list.filter(r => new Date(r.appointmentDatetime) >= startOfDay(dateFrom));
    if (dateTo) list = list.filter(r => new Date(r.appointmentDatetime) <= endOfDay(dateTo));
    return list.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [appointmentResults, tab, search, repFilter, sourceFilter, reasonFilter, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const m = getResultMetrics(appointmentResults, jobs);
    return { total: m.total, sold: m.sold, noSale: m.noSale, rescheduled: m.rescheduled, noDemo: m.noDemo, totalRevenue: m.totalRevenue, closeRate: m.closeRatePercent };
  }, [appointmentResults, jobs]);

  const pagination = usePagination(filtered, 25);

  useEffect(() => {
    pagination.reset();
  }, [tab, search, repFilter, sourceFilter, reasonFilter, dateFrom, dateTo]);

  const ResultBadge = ({ type }: { type: AppointmentResultType }) => {
    const cfg = RESULT_TYPE_CONFIG[type];
    const Icon = RESULT_ICONS[type];
    return (
      <Badge variant="outline" className={cn("gap-1 text-[10px] font-semibold", cfg.color, cfg.bg)}>
        <Icon className="h-3 w-3" /> {cfg.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Appointment Results
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Immutable record of every appointment outcome with downstream status
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {([
          { label: "Total Results", value: stats.total, icon: BarChart3, color: "text-foreground" },
          { label: "Sold", value: stats.sold, icon: CheckCircle, color: "text-success" },
          { label: "No Sale", value: stats.noSale, icon: XCircle, color: "text-destructive" },
          { label: "Rescheduled", value: stats.rescheduled, icon: RotateCcw, color: "text-warning" },
          { label: "No Demo", value: stats.noDemo, icon: AlertTriangle, color: "text-muted-foreground" },
          { label: "Close Rate", value: `${stats.closeRate}%`, icon: TrendingUp, color: "text-primary" },
          { label: "Revenue Won", value: `$${stats.totalRevenue.toLocaleString()}`, icon: DollarSign, color: "text-success" },
        ] as const).map(kpi => (
          <Card key={kpi.label} className="border-border/50">
            <CardContent className="p-3 text-center">
              <kpi.icon className={cn("h-4 w-4 mx-auto mb-1", kpi.color)} />
              <p className="text-lg font-bold">{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground font-medium">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer or rep..." className="pl-8 h-8 text-xs" />
        </div>
        <Select value={repFilter} onValueChange={setRepFilter}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="All Reps" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All Reps</SelectItem>
            {reps.map(r => <SelectItem key={r} value={r} className="text-xs">{repLabel(r)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="All Sources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All Sources</SelectItem>
            {sources.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
              <CalendarIcon className="h-3 w-3" />
              {dateFrom ? format(dateFrom, "MMM d") : "From"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
              <CalendarIcon className="h-3 w-3" />
              {dateTo ? format(dateTo, "MMM d") : "To"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
          </PopoverContent>
        </Popover>
        {(search || repFilter !== "all" || sourceFilter !== "all" || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setSearch(""); setRepFilter("all"); setSourceFilter("all"); setDateFrom(undefined); setDateTo(undefined); setReasonFilter("all"); }}>
            Clear
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-8">
          <TabsTrigger value="all" className="text-xs h-7">All ({appointmentResults.length})</TabsTrigger>
          <TabsTrigger value="sold" className="text-xs h-7">Sold ({stats.sold})</TabsTrigger>
          <TabsTrigger value="no_sale" className="text-xs h-7">No Sale ({stats.noSale})</TabsTrigger>
          <TabsTrigger value="no_demo" className="text-xs h-7">No Demo ({stats.noDemo})</TabsTrigger>
          <TabsTrigger value="rescheduled" className="text-xs h-7">Rescheduled ({stats.rescheduled})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-3">
          {filtered.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                No appointment results match the current filters.
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/50">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">Customer</TableHead>
                      <TableHead className="text-[10px]">Rep</TableHead>
                      <TableHead className="text-[10px]">Result</TableHead>
                      <TableHead className="text-[10px]">Lifecycle</TableHead>
                      <TableHead className="text-[10px]">Source</TableHead>
                      <TableHead className="text-[10px]">Appointment</TableHead>
                      <TableHead className="text-[10px]">Pricing</TableHead>
                      <TableHead className="text-[10px]">Payment</TableHead>
                      <TableHead className="text-[10px]">Reason / Objection</TableHead>
                      <TableHead className="text-[10px]">Next Step</TableHead>
                      <TableHead className="text-[10px]">Submitted</TableHead>
                      <TableHead className="text-[10px] w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagination.paged.map(r => {
                      const ext = extensionsByResultId.get(r.id);
                      const reasonLabel = r.noSaleReason
                        ? (NO_SALE_REASON_LABELS[r.noSaleReason] || r.noSaleReason)
                        : r.noDemoReason
                          ? (NO_DEMO_REASON_LABELS[r.noDemoReason] || r.noDemoReason)
                          : null;
                      const objectionLabel = ext?.objections?.primary
                        ? OBJECTION_LABELS[ext.objections.primary]
                        : null;
                      const lifecycleTone = ext ? lifecycleToneClasses(ext.lifecycle) : null;

                      return (
                        <TableRow
                          key={r.id}
                          className="cursor-pointer hover:bg-muted/40 transition-colors"
                          onClick={() => navigate(`/appointment-outcome/${r.id}`)}
                        >
                          <TableCell className="text-xs font-medium">
                            <Link
                              to={`/appointment-outcome/${r.id}`}
                              className="text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r.customerName}
                            </Link>
                          </TableCell>
                          <TableCell className="text-xs">{repLabel(r.assignedRep)}</TableCell>
                          <TableCell><ResultBadge type={r.resultType} /></TableCell>
                          <TableCell>
                            {lifecycleTone ? (
                              <Badge
                                variant="outline"
                                className={cn("gap-1 text-[9px] font-semibold", lifecycleTone.container, lifecycleTone.text)}
                              >
                                <span className={cn("h-1.5 w-1.5 rounded-full", lifecycleTone.dot)} />
                                {lifecycleTone.label}
                                {ext?.nextStep.managerReviewRequired && (
                                  <ShieldCheck className="h-2.5 w-2.5 ml-0.5" />
                                )}
                              </Badge>
                            ) : (
                              <span className="text-[10px] text-muted-foreground/60">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.source || "Direct"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.appointmentDatetime ? format(parseISO(r.appointmentDatetime), "MMM d, h:mm a") : "N/A"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.resultType === "sold" ? (
                              <div className="space-y-0.5">
                                <p className="text-success font-semibold">${getDisplayPrice(r, jobs).toLocaleString()}</p>
                                {ext?.proposal && ext.proposal.discountAmount > 0 ? (
                                  <p className="text-[9px] text-muted-foreground">${ext.proposal.basePrice.toLocaleString()} − ${ext.proposal.discountAmount.toLocaleString()}</p>
                                ) : ext?.proposal ? (
                                  <p className="text-[9px] text-muted-foreground">{ext.proposal.package}</p>
                                ) : r.pricingSnapshot ? (
                                  <p className="text-[9px] text-muted-foreground">${r.pricingSnapshot.standardPrice.toLocaleString()} list</p>
                                ) : null}
                              </div>
                            ) : ext?.proposal ? (
                              <div className="space-y-0.5">
                                <p className="text-success font-semibold">${ext.proposal.finalPrice.toLocaleString()}</p>
                                {ext.proposal.discountAmount > 0 ? (
                                  <p className="text-[9px] text-muted-foreground">${ext.proposal.basePrice.toLocaleString()} − ${ext.proposal.discountAmount.toLocaleString()}</p>
                                ) : (
                                  <p className="text-[9px] text-muted-foreground">{ext.proposal.package}</p>
                                )}
                              </div>
                            ) : r.pricingSnapshot ? (
                              <div className="space-y-0.5">
                                <p className="text-success font-semibold">${r.pricingSnapshot.bestValuePrice.toLocaleString()}</p>
                                <p className="text-[9px] text-muted-foreground">${r.pricingSnapshot.standardPrice.toLocaleString()} list</p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">No build</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {ext?.proposal?.paymentStructure
                              ? ext.proposal.paymentStructure.charAt(0).toUpperCase() + ext.proposal.paymentStructure.slice(1)
                              : r.pricingSnapshot?.paymentMethod || "N/A"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {objectionLabel ? (
                              <Badge variant="outline" className="gap-1 text-[9px] border-warning/40 bg-warning/5 text-warning">
                                <MessageSquareWarning className="h-2.5 w-2.5" />
                                {objectionLabel}
                              </Badge>
                            ) : reasonLabel ? (
                              <span className="text-muted-foreground">{reasonLabel}</span>
                            ) : r.resultType === "rescheduled" && r.rescheduleDatetime ? (
                              <span className="text-muted-foreground">{format(parseISO(r.rescheduleDatetime), "MMM d")}</span>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={cn(
                              "text-[9px]",
                              r.nextActionStatus === "completed" ? "text-success bg-success/10" :
                              r.nextActionStatus === "in_progress" ? "text-primary bg-primary/10" :
                              "text-warning bg-warning/10"
                            )}>
                              {r.nextAction}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[10px] text-muted-foreground">
                            {format(parseISO(r.submittedAt), "MMM d, h:mm a")}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-[10px] gap-1 text-primary hover:text-primary hover:bg-primary/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/appointment-outcome/${r.id}`);
                              }}
                            >
                              View
                              <ChevronRight className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <PaginationBar {...pagination} />
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Rep Performance Summary */}
      {appointmentResults.length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-4">
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Rep Performance Summary</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">Rep</TableHead>
                    <TableHead className="text-[10px] text-center">Total</TableHead>
                    <TableHead className="text-[10px] text-center">Sold</TableHead>
                    <TableHead className="text-[10px] text-center">No Sale</TableHead>
                    <TableHead className="text-[10px] text-center">Rescheduled</TableHead>
                    <TableHead className="text-[10px] text-center">No Demo</TableHead>
                    <TableHead className="text-[10px] text-center">Close Rate</TableHead>
                    <TableHead className="text-[10px] text-right">Revenue Won</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getRepPerformanceSummary(appointmentResults, jobs).map(r => (
                    <TableRow key={r.rep}>
                      <TableCell className="text-xs font-medium">{r.rep}</TableCell>
                      <TableCell className="text-xs text-center">{r.total}</TableCell>
                      <TableCell className="text-xs text-center text-success font-semibold">{r.sold}</TableCell>
                      <TableCell className="text-xs text-center">{r.noSale}</TableCell>
                      <TableCell className="text-xs text-center">{r.rescheduled}</TableCell>
                      <TableCell className="text-xs text-center">{r.noDemo}</TableCell>
                      <TableCell className="text-xs text-center">
                        <span className={cn("font-semibold", r.closeRate >= 30 ? "text-success" : r.closeRate >= 15 ? "text-warning" : "text-destructive")}>{r.closeRate}%</span>
                      </TableCell>
                      <TableCell className="text-xs text-right font-semibold text-success">${r.revenue.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
